"use client";

import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { useUser } from "@clerk/nextjs";

type MyEvent = {
  id: number;
  storyblokEventUuid: string;
  eventDate: string;
  originalEventDate?: string;
  eventTitle: string;
  eventSlug: string;
  eventType: string;
  isPast: boolean;
  dateChanged: boolean;
  isCancelled: boolean;
  image?: { src: string; alt: string };
  maskShape: string;
  memberOnly: boolean;
};

type MySignupsContextType = {
  signups: Set<string>;
  events: MyEvent[];
  isLoading: boolean;
  loaded: boolean;
  isSignedUp: (eventUuid: string, eventDate: string) => boolean;
  refresh: () => void;
};

const MySignupsContext = createContext<MySignupsContextType>({
  signups: new Set(),
  events: [],
  isLoading: false,
  loaded: false,
  isSignedUp: () => false,
  refresh: () => {},
});

function makeKey(uuid: string, date: string): string {
  return `${uuid}-${date}`;
}

export function MySignupsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const [signups, setSignups] = useState<Set<string>>(new Set());
  const [events, setEvents] = useState<MyEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetchSignups = useCallback(() => {
    if (!user) return;
    setIsLoading(true);
    fetch("/api/events/my-events")
      .then((r) => r.json())
      .then((data) => {
        const eventList: MyEvent[] = data.events || [];
        setEvents(eventList);
        const set = new Set<string>();
        for (const e of eventList) {
          // Use original date as the key (stable identity matching Storyblok date field)
          const keyDate = e.originalEventDate || e.eventDate;
          set.add(makeKey(e.storyblokEventUuid, keyDate));
        }
        setSignups(set);
        setFetched(true);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [user]);

  useEffect(() => {
    if (user && !fetched) {
      fetchSignups();
    }
  }, [user, fetched, fetchSignups]);

  const isSignedUp = useCallback(
    (eventUuid: string, eventDate: string) =>
      signups.has(makeKey(eventUuid, eventDate)),
    [signups]
  );

  return (
    <MySignupsContext.Provider value={{ signups, events, isLoading, loaded: fetched, isSignedUp, refresh: fetchSignups }}>
      {children}
    </MySignupsContext.Provider>
  );
}

export function useMySignups() {
  return useContext(MySignupsContext);
}

export type { MyEvent };
